import { ParseResult } from 'papaparse';
import { EXPECTED_COLUMN_FIELDS, MATCH_THRESHOLD } from './constants';
import { NormalizeFile } from './types';

export function normalizeCSVData(
  papaResult: ParseResult<unknown>,
): NormalizeFile {
  if (
    Array.isArray(papaResult.data) &&
    typeof papaResult.data[0] === 'object' &&
    !Array.isArray(papaResult.data[0])
  ) {
    // header: true
    const headers =
      papaResult.meta?.fields || Object.keys(papaResult.data[0] || {});
    const rows = papaResult.data.map((row) =>
      headers.map((h) => (row as Record<string, unknown>)[h] ?? ''),
    );
    return { headers, rows: rows as string[][] };
  } else if (
    Array.isArray(papaResult.data) &&
    Array.isArray(papaResult.data[0])
  ) {
    // header: false
    const [headers, ...rows] = papaResult.data;
    return { headers, rows: rows as string[][] };
  }
  throw new Error('Unknown PapaParse data structure');
}

export function autoMatchColumns({ headers, rows }: NormalizeFile) {
  const usedColumns = new Set();

  return EXPECTED_COLUMN_FIELDS.map(
    ({ labels, key, valueRegex, allowedValues }) => {
      // Header match
      const headerIdx = headers.findIndex(
        (h, index) =>
          !usedColumns.has(index) &&
          labels.some(
            (label) => h.trim().toLowerCase() === label.trim().toLowerCase(),
          ),
      );
      if (headerIdx !== -1) {
        usedColumns.add(headerIdx);
        return {
          field: key,
          matched: true,
          matchedBy: 'header',
          columnIndex: headerIdx,
          columnHeader: headers[headerIdx],
        };
      }

      // Regex match
      let regexes: RegExp[] = [];
      if (valueRegex) {
        if (Array.isArray(valueRegex)) {
          regexes = valueRegex;
        } else {
          regexes = [valueRegex];
        }
      }

      if (regexes.length > 0) {
        for (let colIdx = 0; colIdx < headers.length; colIdx += 1) {
          if (!usedColumns.has(colIdx)) {
            const colValues =
              rows?.map((row) => row[colIdx]).filter(Boolean) ?? [];
            const matches = colValues.filter((val) =>
              regexes.some((re) => re.test(val)),
            );
            if (
              matches.length > 0 &&
              matches.length >= Math.floor(colValues.length * MATCH_THRESHOLD)
            ) {
              usedColumns.add(colIdx);
              return {
                field: key,
                matched: true,
                matchedBy: 'regex',
                columnIndex: colIdx,
                columnHeader: headers[colIdx],
              };
            }
          }
        }
      }

      // Allowed values match
      if (allowedValues && Array.isArray(allowedValues)) {
        let foundIdx = -1;
        for (let colIdx = 0; colIdx < headers.length; colIdx += 1) {
          if (!usedColumns.has(colIdx)) {
            const colValues = rows?.map((row) =>
              String(row[colIdx] || '').toLowerCase(),
            );
            const matches = colValues?.filter((val) =>
              allowedValues.includes(val),
            );
            if (
              matches &&
              matches.length > 0 &&
              matches.length >=
                Math.floor((colValues || []).length * MATCH_THRESHOLD)
            ) {
              foundIdx = colIdx;
              break;
            }
          }
        }
        if (foundIdx !== -1) {
          usedColumns.add(foundIdx);
          return {
            field: key,
            matched: true,
            matchedBy: 'allowedValues',
            columnIndex: foundIdx,
            columnHeader: headers[foundIdx],
          };
        }
      }

      // Not matched
      return {
        field: key,
        matched: false,
        matchedBy: null,
        columnIndex: null,
        columnHeader: null,
      };
    },
  );
}
